/**
 * Feature Gating Utility
 * Enforces free plan limits:
 * - 6 cameras
 * - 2 equipment total
 * - 1 fence
 * - 1 gate
 * - 1 building
 * - 1 cable route
 * - 1 project
 *
 * While subscription is loading, actions are ALLOWED (optimistic).
 * Once loaded, real limits apply.
 */

export interface FeatureCheckResult {
  allowed: boolean;
  reason?: string;
  daysRemaining?: number;
}

export interface SubscriptionState {
  isPaid: boolean;
  isLite: boolean;
  isStandard?: boolean;
  isPremium?: boolean;
  isPro?: boolean;
  isTrial: boolean; // true for free plan users
  isTrialExpired: boolean; // always false — free plan never expires
  trialDaysRemaining: number; // always 0
  isLoading?: boolean;
}

export interface ProjectUsage {
  cameraCount: number;
  fenceCount: number;
  gateCount: number;
  buildingCount: number;
  equipmentCount: number;
  cableRouteCount: number;
}

export const FREE_LIMITS = {
  cameras: 6,
  fences: 1,
  gates: 1,
  buildings: 1,
  equipment: 2,
  cableRoutes: 1,
};

// Alias for backwards compatibility
export const TRIAL_LIMITS = FREE_LIMITS;

export const LITE_LIMITS = {
  cameras: 16,
  projects: 5,
};

/**
 * Per-plan limits on the number of floors a single project may contain.
 * Multi-floor is a Premium-and-above feature. Free / Lite / Standard users
 * are limited to a single floor.
 */
export const FLOOR_LIMITS = {
  free: 1,
  lite: 1,
  standard: 1,
  premium: 20,
  pro: 20,
};

export function getMaxFloorsForSubscription(subscription: SubscriptionState): number {
  if (subscription.isPro || subscription.isPremium) return FLOOR_LIMITS.premium;
  if (subscription.isStandard) return FLOOR_LIMITS.standard;
  if (subscription.isLite) return FLOOR_LIMITS.lite;
  return FLOOR_LIMITS.free;
}

// Plan label for analytics (funnel_events.properties.plan). SubscriptionState
// is a bag of booleans, so call sites kept reading `subscription.plan` and
// getting `undefined` — making the /admin/funnel dashboard unable to group
// feature_used by plan. Centralised here so every emitter labels the same way.
export function planNameOf(s: SubscriptionState): 'pro' | 'premium' | 'standard' | 'lite' | 'free' {
  if (s.isPro) return 'pro';
  if (s.isPremium) return 'premium';
  if (s.isStandard) return 'standard';
  if (s.isLite) return 'lite';
  return 'free';
}

/**
 * Check whether the current user may switch the multi-floor feature on for
 * their project. Independent of how many floors they already have — this is
 * the gate for the toggle itself. Premium-and-above only.
 */
export function canUseMultiFloor(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason:
      'Multi-floor design requires Premium plan or higher. Upgrade to plan buildings with several floors, each with its own floor plan, cameras, and cables.',
  };
}

/**
 * Check if user can add another floor to the project.
 */
export function canAddFloor(
  subscription: SubscriptionState,
  currentFloorCount: number,
): FeatureCheckResult {
  if (subscription.isLoading) {
    return currentFloorCount < FLOOR_LIMITS.free
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }
  const max = getMaxFloorsForSubscription(subscription);
  if (currentFloorCount >= max) {
    if (max === 1) {
      return {
        allowed: false,
        reason:
          'Multi-floor plans require Premium plan or higher. Upgrade to design buildings with multiple floors in a single project.',
      };
    }
    return {
      allowed: false,
      reason: `Floor limit reached (${max} floors per project). Upgrade for a higher cap.`,
    };
  }
  return { allowed: true };
}

/**
 * Grace period (in days) after a subscription goes past_due
 * before features are restricted.
 */
export const PAST_DUE_GRACE_PERIOD_DAYS = 3;

/**
 * Check if a past-due subscription is still within the grace period.
 * @param updatedAt - ISO date string of last subscription update
 * @returns true if within grace period, false otherwise
 */
export function isInGracePeriod(updatedAt: string | undefined): boolean {
  if (!updatedAt || updatedAt === 'null') return false;
  const updatedDate = new Date(updatedAt);
  if (isNaN(updatedDate.getTime())) return false;
  const now = new Date();
  const diffMs = now.getTime() - updatedDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays < PAST_DUE_GRACE_PERIOD_DAYS;
}

/**
 * Check if user can add a camera to the project
 */
export function canAddCamera(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  // While loading, enforce free plan limits as a safe default.
  // Server-side validation is the final gate; this prevents client-side exploits.
  if (subscription.isLoading) {
    return usage.cameraCount < FREE_LIMITS.cameras
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite) {
    if (usage.cameraCount >= LITE_LIMITS.cameras) {
      return {
        allowed: false,
        reason: `Lite plan limit reached (${LITE_LIMITS.cameras} cameras per project). Upgrade to Standard for unlimited cameras.`,
      };
    }
    return { allowed: true };
  }

  if (subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.cameraCount >= FREE_LIMITS.cameras) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.cameras} cameras per project). Upgrade to add more cameras.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can add a fence to the project
 */
export function canAddFence(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.fenceCount < FREE_LIMITS.fences
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.fenceCount >= FREE_LIMITS.fences) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.fences} fence per project). Upgrade to add more fences.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can add a gate to the project
 */
export function canAddGate(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.gateCount < FREE_LIMITS.gates
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.gateCount >= FREE_LIMITS.gates) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.gates} gate per project). Upgrade to add more gates.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can add a building to the project
 */
export function canAddBuilding(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.buildingCount < FREE_LIMITS.buildings
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.buildingCount >= FREE_LIMITS.buildings) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.buildings} building per project). Upgrade to add more buildings.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can add equipment to the project
 */
export function canAddEquipment(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.equipmentCount < FREE_LIMITS.equipment
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.equipmentCount >= FREE_LIMITS.equipment) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.equipment} equipment items). Upgrade to add more equipment.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can add a cable route to the project
 */
export function canAddCableRoute(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.cableRouteCount < FREE_LIMITS.cableRoutes
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.cableRouteCount >= FREE_LIMITS.cableRoutes) {
      return {
        allowed: false,
        reason: `Free plan limit reached (${FREE_LIMITS.cableRoutes} cable route per project). Upgrade to add more cable routes.`,
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: "Unable to verify subscription. Please refresh the page." };
}

/**
 * Check if user can save a project
 */
export function canSaveProject(
  subscription: SubscriptionState,
  existingProjectCount: number = 0,
  isUpdatingExisting: boolean = false
): FeatureCheckResult {
  // While loading, allow updates but restrict new projects to free limit (1)
  if (subscription.isLoading) {
    if (isUpdatingExisting) return { allowed: true };
    return existingProjectCount < 1
      ? { allowed: true }
      : { allowed: false, reason: 'Loading subscription...' };
  }

  if (subscription.isLite) {
    if (isUpdatingExisting) return { allowed: true };
    if (existingProjectCount >= LITE_LIMITS.projects) {
      return {
        allowed: false,
        reason: `Lite plan limit reached (${LITE_LIMITS.projects} projects max). Upgrade to Standard for unlimited projects.`,
      };
    }
    return { allowed: true };
  }

  if (subscription.isPaid) {
    return { allowed: true };
  }

  if (isUpdatingExisting) {
    return { allowed: true };
  }

  if (existingProjectCount >= 1) {
    return {
      allowed: false,
      reason: "Free plan limit reached (1 project maximum). Upgrade to create more projects.",
    };
  }

  return { allowed: true };
}

/**
 * Check if user can export to PDF
 */
export function canExportPDF(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

/**
 * Check if user can access the planner at all
 */
export function canAccessPlanner(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

/**
 * Check if user can use Survey Mode (Standard+ plans)
 */
export function canUseSurveyMode(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPaid && !subscription.isLite) return { allowed: true };
  return {
    allowed: false,
    reason: 'Survey Mode requires Standard plan or higher. Upgrade to capture photos, voice notes, and checklists on-site.',
  };
}

/**
 * Check if user can use Client Portal (Premium+ plans)
 */
export function canUseClientPortal(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: 'Client Portal requires Premium plan or higher. Upgrade to share read-only project links with clients.',
  };
}

/**
 * Check if user can use System Validator (Premium+ plans).
 * (Previously commented as "Standard+" — that contradicted both the code
 * below and the user-facing reason string. Premium is the truth.)
 */
export function canUseSystemValidator(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: 'System Validator requires Premium plan. Upgrade to automatically detect coverage gaps, DORI issues, and cable problems.',
  };
}

/**
 * Check if user can use BOM (Premium+ plans)
 */
export function canUseBOM(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: 'Bill of Materials requires Premium plan. Upgrade to generate hardware lists with storage calculations.',
  };
}

/**
 * Check if user can use DXF/CAD export (Premium+ plans).
 * Full-fidelity vector export with layers, blocks, DORI zones, obstacle clipping,
 * multi-floor ZIP packaging and georeferenced floor plan raster embedding.
 */
export function canUseDXFExport(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason:
      'DXF export requires Premium plan. Upgrade to export your design to CAD (AutoCAD, BricsCAD, ArchiCAD, Revit, QGIS) with layers, blocks, DORI zones, obstacle clipping and multi-floor packaging.',
  };
}

/**
 * Check if user can use Smart Quote (Premium+ plans)
 */
export function canUseSmartQuote(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: 'Loading subscription...' };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: 'Smart Quote requires Premium plan. Upgrade to generate professional quotes with hardware pricing, labor estimates, and margin calculation.',
  };
}

/**
 * Get a summary of free plan limits and current usage for a project
 */
export function getTrialLimitsSummary(usage: ProjectUsage): string {
  return (
    `Cameras: ${usage.cameraCount}/${FREE_LIMITS.cameras} • ` +
    `Fences: ${usage.fenceCount}/${FREE_LIMITS.fences} • ` +
    `Gates: ${usage.gateCount}/${FREE_LIMITS.gates} • ` +
    `Buildings: ${usage.buildingCount}/${FREE_LIMITS.buildings}`
  );
}
